import { ActionIcon, Menu, UnstyledButton, VisuallyHidden } from "@mantine/core";
import {
  Blend,
  Bot,
  Camera,
  Captions,
  Check,
  Copy,
  Ellipsis,
  FolderOpen,
  Gauge,
  Keyboard,
  Magnet,
  Maximize,
  Pause,
  Play,
  Redo2,
  Bell,
  Save,
  Scissors,
  SeparatorVertical,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  Type,
  Undo2,
  Volume2,
} from "lucide-react";
import type { ReactNode } from "react";

import { PHONE_TABS, type PhoneAction, type SheetName } from "./phone";
import { formatTimecode, formatTotalTimecode } from "./time";
import classes from "./editor.module.css";

/**
 * The phone's "⋯" (spec phone-layout.md): the card's Save, Keyboard shortcuts, Notifications and
 * the theme, then the desktop's Snapshot, Snapping and the main track magnet, which the card
 * places nowhere else (decision 3). An item with no handler cannot run now, as its desktop control cannot.
 */
export function PhoneMenu(props: {
  onSave: (() => void) | undefined;
  onShortcuts: () => void;
  onNotifications: () => void;
  notifications: string;
  theme: { label: string; Icon: (props: { size: number }) => ReactNode; flip: () => void };
  onSnapshot: (() => void) | undefined;
  snapping: boolean;
  onSnapping: () => void;
  /** The main track magnet's switch (spec magnet-ripple), for who may edit; else none. */
  magnet: { on: boolean; onToggle: () => void } | undefined;
}) {
  const { theme } = props;
  return (
    <Menu position="bottom-end" width={240} shadow="md">
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray" size={44} aria-label="Editor menu">
          <Ellipsis size={20} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item leftSection={<Save size={16} />} disabled={props.onSave === undefined} onClick={props.onSave}>
          Save
        </Menu.Item>
        <Menu.Item leftSection={<Keyboard size={16} />} onClick={props.onShortcuts}>
          Keyboard shortcuts
        </Menu.Item>
        <Menu.Item leftSection={<Bell size={16} />} onClick={props.onNotifications}>
          {props.notifications}
        </Menu.Item>
        <Menu.Item leftSection={<theme.Icon size={16} />} onClick={theme.flip}>
          {theme.label}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          leftSection={<Camera size={16} />}
          disabled={props.onSnapshot === undefined}
          onClick={props.onSnapshot}
         
        >
          Snapshot
        </Menu.Item>
        <Menu.Item
          leftSection={<SeparatorVertical size={16} />}
          rightSection={props.snapping ? <Check size={16} aria-hidden /> : null}
          onClick={props.onSnapping}
        >
          Snapping
          {/* Its state, as the desktop's switch tells it by being pressed. */}
          <VisuallyHidden>{props.snapping ? ", on" : ", off"}</VisuallyHidden>
        </Menu.Item>
        {props.magnet === undefined ? null : (
          <Menu.Item
            leftSection={<Magnet size={16} />}
            rightSection={props.magnet.on ? <Check size={16} aria-hidden /> : null}
            onClick={props.magnet.onToggle}
          >
            Main track magnet
            <VisuallyHidden>{props.magnet.on ? ", on" : ", off"}</VisuallyHidden>
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

/** A 44 px icon button of the phone's rows, named for its action. */
function RowTool(props: { label: string; onClick: (() => void) | undefined; children: ReactNode }) {
  return (
    <ActionIcon
      variant="subtle"
      color="gray"
      size={44}
      aria-label={props.label}
      disabled={props.onClick === undefined}
      onClick={props.onClick}
    >
      {props.children}
    </ActionIcon>
  );
}

/** The card's transport row, between the canvas and the timeline: play and the time, then Undo, Redo and full screen. */
export function PhoneTransport(props: {
  playing: boolean;
  onPlay: (() => void) | undefined;
  playheadMs: number;
  totalMs: number;
  onUndo: (() => void) | undefined;
  onRedo: (() => void) | undefined;
  /** Where the browser has full screen: the canvas tool's own command. */
  onFullScreen: (() => void) | undefined;
}) {
  return (
    <div className={classes.phoneTransport} role="toolbar" aria-label="Playback">
      <div className={classes.phoneTransportGroup}>
        <RowTool label={props.playing ? "Pause" : "Play"} onClick={props.onPlay}>
          {props.playing ? <Pause size={18} /> : <Play size={18} />}
        </RowTool>
        <span className={classes.clock}>
          <span className={classes.clockNow}>{formatTimecode(props.playheadMs, true)}</span>
          {" / "}
          <span className={classes.clockTotal}>{formatTotalTimecode(props.totalMs)}</span>
        </span>
      </div>
      <div className={classes.phoneTransportGroup}>
        <RowTool label="Undo" onClick={props.onUndo}>
          <Undo2 size={18} />
        </RowTool>
        <RowTool label="Redo" onClick={props.onRedo}>
          <Redo2 size={18} />
        </RowTool>
        {props.onFullScreen === undefined ? null : (
          <RowTool label="Full screen" onClick={props.onFullScreen}>
            <Maximize size={18} />
          </RowTool>
        )}
      </div>
    </div>
  );
}

const TAB_ICONS: Record<string, ReactNode> = {
  media: <FolderOpen size={18} />,
  text: <Type size={18} />,
  captions: <Captions size={18} />,
  transitions: <Blend size={18} />,
  effects: <Sparkles size={18} />,
  assistant: <Bot size={18} />,
};

/** The bottom tabs with nothing chosen: each opens its panel as a sheet, or closes it again. */
export function PhoneTabs(props: { open: SheetName | null; onOpen: (sheet: SheetName | null) => void }) {
  return (
    <nav className={classes.phoneRow} aria-label="Panels">
      {PHONE_TABS.map((tab) => (
        <UnstyledButton
          key={tab.sheet}
          className={classes.phoneTab}
          aria-pressed={props.open === tab.sheet}
          onClick={() => props.onOpen(props.open === tab.sheet ? null : tab.sheet)}
        >
          {TAB_ICONS[tab.sheet]}
          <span>{tab.label}</span>
        </UnstyledButton>
      ))}
    </nav>
  );
}

const ACTIONS: Record<PhoneAction, { label: string; icon: ReactNode }> = {
  done: { label: "Done", icon: <Check size={18} /> },
  split: { label: "Split", icon: <Scissors size={18} /> },
  speed: { label: "Speed", icon: <Gauge size={18} /> },
  volume: { label: "Volume", icon: <Volume2 size={18} /> },
  duplicate: { label: "Duplicate", icon: <Copy size={18} /> },
  delete: { label: "Delete", icon: <Trash2 size={18} /> },
  parameters: { label: "Parameters", icon: <SlidersHorizontal size={18} /> },
  captions: { label: "Captions", icon: <Captions size={18} /> },
  more: { label: "More", icon: <Ellipsis size={18} /> },
};

/**
 * The chosen's actions, which replace the tabs (the card's, decision 2). Each runs what its
 * desktop control runs; one with no handler cannot run on this choice now. Where they do not fit
 * (eight on a 320 px phone), the row scrolls sideways (decision 10). Speed opens the Parameters
 * at its Speed section (spec clip-speed-curves-tab).
 */
export function PhoneActions(props: {
  actions: readonly PhoneAction[];
  run: Partial<Record<PhoneAction, ((anchor: HTMLElement) => void) | undefined>>;
}) {
  return (
    <div className={classes.phoneActions} role="toolbar" aria-label="Chosen clip">
      {props.actions.map((action) => {
        const { label, icon } = ACTIONS[action];
        const handler = props.run[action];
        return (
          <UnstyledButton
            key={action}
            className={classes.phoneTab}
            disabled={handler === undefined}
            onClick={(event) => handler?.(event.currentTarget)}
          >
            {icon}
            <span>{label}</span>
          </UnstyledButton>
        );
      })}
    </div>
  );
}
